/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useCallback } from 'preact/hooks';
import { getInfo } from '#utils/UNSAFE_visUtils';
import { ChartGroupInfo, Info, GroupDetail, ItemDetail } from '#utils/UNSAFE_visTypes/chart';
import { useVisDrill } from '#hooks/PRIVATE_useVisDrill';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';

type DrillEventsProps<K, D> = {
  rootRef: RefObject<HTMLDivElement>;
  levelsArray: GroupLevelItem[][];
  focusedItemInfo: Info;
  focusedGroupInfo: ChartGroupInfo;
  selectionMode: 'none' | 'single' | 'multiple';
  getDataItem: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined;
  drilling: 'on' | 'groupsOnly' | 'off';
  onItemDrill?: (detail: ItemDetail<K, D>) => void;
  onGroupDrill?: (detail: GroupDetail) => void;
};

const isDrillable = <K, D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>>(
  info: { seriesIndex?: number; groupIndex?: number; levelIndex?: number },
  drilling: 'on' | 'groupsOnly' | 'off',
  levelsArray: GroupLevelItem[][],
  getDataItem: (seriesIndex: number, groupIndex: number, path: number[]) => D | undefined
) => {
  const { seriesIndex, groupIndex, levelIndex } = info;

  if (groupIndex === undefined || levelsArray.length < 1) {
    return false;
  }

  const groupInfo = levelsArray[levelsArray.length - 1][groupIndex];
  if (seriesIndex != null) {
    const itemDrilling = getDataItem(seriesIndex, groupIndex, groupInfo.path)?.drilling != 'off';
    return drilling === 'on' && itemDrilling;
  } else if (levelIndex !== undefined) {
    return levelsArray[levelIndex][groupIndex].item.drilling != 'off';
  }

  return false;
};

/**
 * Returns the event handlers for drill support.
 */
export function useChartDrill<K, D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>>({
  rootRef,
  focusedItemInfo,
  focusedGroupInfo,
  levelsArray,
  selectionMode,
  drilling,
  getDataItem,
  onItemDrill,
  onGroupDrill
}: DrillEventsProps<K, D>) {
  const processDrill = useCallback(
    (info?: { seriesIndex?: number; groupIndex?: number; levelIndex?: number }) => {
      if (
        !info ||
        info.groupIndex == undefined ||
        !isDrillable(info, drilling, levelsArray, getDataItem)
      ) {
        return;
      }

      const { seriesIndex, groupIndex, levelIndex } = info;
      const groupInfo =
        levelsArray[levelIndex != undefined ? levelIndex : levelsArray.length - 1][groupIndex];
      if (seriesIndex != null) {
        const data = getDataItem(seriesIndex, groupIndex, groupInfo.path);
        if (data) {
          onItemDrill?.({
            groupIndex,
            seriesIndex,
            data,
            id: data?.id,
            groupPath: groupInfo.path
          });
        }
      } else {
        onGroupDrill?.({
          groupIndex,
          group: groupInfo.item,
          id: groupInfo.item.id,
          groupPath: groupInfo.path
        });
      }
    },
    [drilling, getDataItem, onGroupDrill, onItemDrill, levelsArray]
  );

  const onDrill = useCallback(
    (event: Event) => {
      if (event.type === 'keydown') {
        const focusedInfo = focusedItemInfo.isCurrent ? focusedItemInfo : focusedGroupInfo;
        processDrill(focusedInfo);
      } else {
        processDrill(getInfo(rootRef, event.target as HTMLElement));
      }
    },
    [rootRef, focusedItemInfo, focusedGroupInfo, processDrill]
  );

  return useVisDrill(selectionMode, onDrill);
}
